<script setup lang="ts">
import moment from 'moment';
import { computed, onMounted, ref, unref, watch } from 'vue';

import BootstrapDatepicker from './bootstrap-datepicker.vue';
import BootstrapSelect2 from './bootstrap-select2.vue';
import type { FixedPeriodTableType } from './period-filter-control';
import {
    DefaultFixedPeriodTable, NullFixedPeriod, SelectedPeriod,
    TuttiFixedPeriod,
} from './period-filter-control';

const props = defineProps<{
    periodTable?: FixedPeriodTableType;
    modelValue?: SelectedPeriod;
    selectedPeriod?: string;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: SelectedPeriod): void;
    (event: 'update:selectedPeriod', value: string | undefined): void;
}>();

const selectedPickerRef = ref(pickerFromValue(props.modelValue, props.selectedPeriod));
const selectedPeriodRef = ref(periodFromValue(props.selectedPeriod));
const periodTableRef = computed(() => props.periodTable ?? DefaultFixedPeriodTable);

function pickerFromValue(value: SelectedPeriod | undefined, period: string | undefined) {
    if(value) {
        return new SelectedPeriod(value).toMoment();
    }
    return SelectedPeriod.fromFixedPeriod2(undefined, periodFromValue(period), -1);
}

function periodFromValue(period: string | undefined) {
    return (!period || (period == TuttiFixedPeriod)) ? NullFixedPeriod : period;
}

function emitUpdatePicker() {
    const _selectedValue = SelectedPeriod.fromMoment(unref(selectedPickerRef));
    if(!SelectedPeriod.compare(props.modelValue, _selectedValue)) {
        emit('update:modelValue', _selectedValue);
    }
}

function emitUpdatePeriod() {
    const _selectedPeriod = unref(selectedPeriodRef);
    if(_selectedPeriod == props.selectedPeriod) {
        return;
    }
    emit('update:selectedPeriod', _selectedPeriod);
}

function pickerChangedExternally() {
    const from = moment(unref(selectedPickerRef).from);
    const to = moment(unref(selectedPickerRef).to);
    const diff = to.diff(from, 'day');
    for(const period of Object.keys(unref(periodTableRef))) {
        if(diff == Number(period)) {
            selectedPeriodRef.value = period;
            emitUpdatePeriod();
            break;
        }
    }
    emitUpdatePicker();
}

function periodChanged(period?: string | string[]) {
    const _selectedPeriod = (typeof period === 'string') ? period : unref(selectedPeriodRef);
    selectedPeriodRef.value = _selectedPeriod;
    syncPickers(unref(selectedPickerRef).to, -1);
    emitUpdatePeriod();
}

function dateChanged() {
    selectedPeriodRef.value = NullFixedPeriod;
    emitUpdatePeriod();
    emitUpdatePicker();
}

function syncPickers(base: Date, sign: number) {
    if(unref(selectedPeriodRef) !== NullFixedPeriod) {
        selectedPickerRef.value = SelectedPeriod.fromFixedPeriod2(base, unref(selectedPeriodRef), sign);
    }
    emitUpdatePicker();
}

function movePickers(sign: number) {
    selectedPickerRef.value = SelectedPeriod.movePickers(unref(selectedPickerRef), sign);
    emitUpdatePicker();
}

watch(() => [props.modelValue, props.selectedPeriod] as const, (val, old) => {
    selectedPickerRef.value = pickerFromValue(props.modelValue, props.selectedPeriod);
    selectedPeriodRef.value = periodFromValue(props.selectedPeriod);
    if(val[0] !== old[0]) {
        if(val[1] === old[1]) {
            pickerChangedExternally();
        } else {
            emitUpdatePicker();
        }
    }
    if(val[1] !== old[1]) {
        periodChanged();
    }
});

onMounted(() => {
    emitUpdatePicker();
    emitUpdatePeriod();
});
</script>

<template>
    <div class="form-group">
        <div class="col-md-3">
            <div class="input-group">
                <span class="input-group-btn">
                    <button type="button" class="btn btn-default" title="Move backward" @click.stop="movePickers(-1)">
                        <span class="bi bi-chevron-left" />
                    </button>
                </span>
                <BootstrapDatepicker
                    v-model:moment-value="selectedPickerRef.from"
                    type="date"
                    appearance="simple"
                    @update:moment-value="dateChanged()"
                />
            </div>
        </div>
        <div class="col-md-3">
            <div class="input-group">
                <BootstrapDatepicker
                    v-model:moment-value="selectedPickerRef.to"
                    type="date"
                    appearance="simple"
                    @update:moment-value="dateChanged()"
                />
                <span class="input-group-btn">
                    <button type="button" class="btn btn-default" title="Move forward" @click.stop="movePickers(1)">
                        <span class="bi bi-chevron-right" />
                    </button>
                </span>
            </div>
        </div>
        <div class="col-md-3">
            <BootstrapSelect2 v-model="selectedPeriodRef" @update:model-value="periodChanged">
                <option :value="NullFixedPeriod">
                    --
                </option>
                <template v-for="(value, key) in periodTableRef" :key="key">
                    <option :value="key">
                        {{ value.next }}
                    </option>
                </template>
            </BootstrapSelect2>
        </div>
        <div class="col-md-2">
            <div class="btn-toolbar" role="toolbar">
                <div class="btn-group">
                    <slot name="buttons" />
                </div>
            </div>
        </div>
    </div>
</template>
